/**
 * Servizio centralizzato per la gestione del localStorage
 * Utilizza use-local-storage-state per gestire SSR/hydration in Next.js
 */

import createLocalStorageStateHook from 'use-local-storage-state'

// Tipi per le chiavi supportate
export type LocalStorageKey = 'debug' | 'autoUpdate' | 'debugPdf' | 'theme' | 'language'

// Configurazione delle chiavi con i loro valori di default
export const LOCAL_STORAGE_CONFIG = {
  debug: { defaultValue: false, storageSync: true },
  autoUpdate: { defaultValue: true, storageSync: true },
  debugPdf: { defaultValue: false, storageSync: true },
  theme: { defaultValue: 'light', storageSync: true },
  language: { defaultValue: 'it', storageSync: true },
} as const

// Funzione per generare chiavi specifiche per template o context
export const generateStorageKey = (baseKey: LocalStorageKey, context?: string | null): string => {
  return context ? `tvd_${baseKey}_${context}` : `tvd_${baseKey}_global`
}

// Hook generici per ogni tipo di dato
export const useDebugStorage = (context?: string) =>
  createLocalStorageStateHook(generateStorageKey('debug', context), LOCAL_STORAGE_CONFIG.debug)

export const useAutoUpdateStorage = (context?: string) =>
  createLocalStorageStateHook(
    generateStorageKey('autoUpdate', context),
    LOCAL_STORAGE_CONFIG.autoUpdate
  )

export const useDebugPdfStorage = () =>
  createLocalStorageStateHook(generateStorageKey('debugPdf'), LOCAL_STORAGE_CONFIG.debugPdf)

export const useThemeStorage = () =>
  createLocalStorageStateHook(generateStorageKey('theme'), LOCAL_STORAGE_CONFIG.theme)

export const useLanguageStorage = () =>
  createLocalStorageStateHook(generateStorageKey('language'), LOCAL_STORAGE_CONFIG.language)

// Utility per accesso diretto (senza hook)
export class LocalStorageService {
  private static getKey(baseKey: LocalStorageKey, context?: string): string {
    return generateStorageKey(baseKey, context)
  }

  static get<T>(key: LocalStorageKey, context?: string): T | null {
    if (typeof window === 'undefined') return null

    try {
      const item = localStorage.getItem(this.getKey(key, context))
      return item ? JSON.parse(item) : null
    } catch (error) {
      console.warn(`LocalStorage get error for key ${key}:`, error)
      return null
    }
  }

  static set<T>(key: LocalStorageKey, value: T, context?: string): void {
    if (typeof window === 'undefined') return

    try {
      localStorage.setItem(this.getKey(key, context), JSON.stringify(value))
    } catch (error) {
      console.warn(`LocalStorage set error for key ${key}:`, error)
    }
  }

  static remove(key: LocalStorageKey, context?: string): void {
    if (typeof window === 'undefined') return

    try {
      localStorage.removeItem(this.getKey(key, context))
    } catch (error) {
      console.warn(`LocalStorage remove error for key ${key}:`, error)
    }
  }

  static clear(): void {
    if (typeof window === 'undefined') return

    try {
      // Rimuovi solo le chiavi TVD
      const keys = Object.keys(localStorage).filter(key => key.startsWith('tvd_'))
      keys.forEach(key => localStorage.removeItem(key))
    } catch (error) {
      console.warn('LocalStorage clear error:', error)
    }
  }

  // Utility per debug
  static getAllTvdKeys(): Record<string, any> {
    if (typeof window === 'undefined') return {}

    const result: Record<string, any> = {}
    try {
      const keys = Object.keys(localStorage).filter(key => key.startsWith('tvd_'))
      keys.forEach(key => {
        try {
          result[key] = JSON.parse(localStorage.getItem(key) || 'null')
        } catch {
          result[key] = localStorage.getItem(key)
        }
      })
    } catch (error) {
      console.warn('LocalStorage getAllTvdKeys error:', error)
    }

    return result
  }
}

// Hook compositi per casi d'uso specifici
export const usePdfDocumentStorage = (template?: string) => {
  const [debug, setDebug] = useDebugStorage(template)
  const [autoUpdate, setAutoUpdate] = useAutoUpdateStorage(template)

  return {
    debug,
    setDebug,
    autoUpdate,
    setAutoUpdate,
    // Utility per reset
    resetToDefaults: () => {
      setDebug(LOCAL_STORAGE_CONFIG.debug.defaultValue)
      setAutoUpdate(LOCAL_STORAGE_CONFIG.autoUpdate.defaultValue)
    },
  }
}

// Logging per debug (da usare in development)
export const logStorageState = () => {
  if (process.env.NODE_ENV === 'development') {
    console.log('📦 TVD LocalStorage State:', LocalStorageService.getAllTvdKeys())
  }
}
